/**
 * DelegationLinkChip
 *
 * Renders clickable pills below assistant messages that responded to a
 * delegation completion. Two render modes:
 *
 *  - In-place updates (`delegationRef.surfaceRefs` present): one chip per
 *    referenced surface, click scrolls the canvas panel to that surface.
 *  - New-canvas delegation (default): single chip whose click switches
 *    the canvas panel to the delegation's canvas.
 */

import { cleanDisplayText } from '@/utils/display-text';
import { useCallback } from 'react';
import { useAuiState } from '@assistant-ui/react';
import { Bot, RefreshCw } from 'lucide-react';
import { useCanvases } from '../../contexts/CanvasContext';
import type { DelegationRef } from '../../types';

export function DelegationLinkChip() {
  const messageMetadata = useAuiState((s) => s.message.metadata);
  const { canvases, surfaces, setActiveCanvasId, loadCanvasSurfaces, requestScrollToSurface } =
    useCanvases();

  const delegationRef = (messageMetadata?.custom as Record<string, unknown> | undefined)
    ?.delegationRef as DelegationRef | undefined;

  const handleSwitch = useCallback(() => {
    if (!delegationRef) return;
    const { canvasId } = delegationRef;
    const hasSurfaces = [...surfaces.values()].some((s) => s.canvasId === canvasId);
    if (!hasSurfaces) loadCanvasSurfaces(canvasId);
    setActiveCanvasId(canvasId);
  }, [delegationRef, surfaces, setActiveCanvasId, loadCanvasSurfaces]);

  const handleSurfaceClick = useCallback(
    (canvasId: string, surfaceId: string) => {
      requestScrollToSurface(canvasId, surfaceId);
    },
    [requestScrollToSurface],
  );

  if (!delegationRef || !canvases.has(delegationRef.canvasId)) return null;

  const surfaceRefs = delegationRef.surfaceRefs ?? [];

  if (surfaceRefs.length > 0) {
    return (
      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        {surfaceRefs.map((ref) => (
          <button
            key={ref.surfaceId}
            type="button"
            className="focus-ring inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/50 px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            onClick={() => handleSurfaceClick(delegationRef.canvasId, ref.surfaceId)}
          >
            <RefreshCw className="h-3 w-3" />
            {cleanDisplayText(ref.title) || ref.surfaceName}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="mt-3 flex flex-wrap items-center gap-1.5">
      <button
        type="button"
        className="focus-ring inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/50 px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        onClick={handleSwitch}
      >
        <Bot className="h-3 w-3" />
        {cleanDisplayText(delegationRef.title) || delegationRef.agentName}
      </button>
    </div>
  );
}
